export type CaseCategory =
  | "fraud_money_laundering"
  | "drug"
  | "sexual_offense"
  | "violence_family"
  | "traffic_negligence"
  | "weapons"
  | "financial_banking"
  | "environmental"
  | "other_criminal";

export type ProcedureStage =
  | "investigation"
  | "detention"
  | "first_instance"
  | "appeal"
  | "supreme_court"
  | "post_judgment";

export interface PublicCase {
  id: string;
  lawyerId: string;
  court: string;
  caseNo: string;
  date: string;
  cause: string;
  category: CaseCategory;
  stage: ProcedureStage;
  role: string;
  representedParty: string;
  legalAid: boolean;
  outcomeTags: string[];
  outcomeSummary: string;
  resultQuality: "strong" | "mixed" | "limited" | "unknown";
  sourceUrl: string;
  evidenceText: string;
}

export interface Lawyer {
  id: string;
  name: string;
  barNo?: string;
  officeName: string;
  guild?: string;
  status: "active" | "inactive" | "unknown";
  location: string;
  sourceUpdatedAt: string;
  summary: string;
  practiceTags: string[];
  riskFlags: string[];
  dataNotice?: string;
}

export interface LawyerProfile extends Lawyer {
  cases: PublicCase[];
  stats: LawyerStats;
}

export interface LawyerStats {
  totalPublicCriminalRoles: number;
  recentRoleCount: number;
  legalAidCount: number;
  categoryCounts: Record<CaseCategory, number>;
  courtCounts: Record<string, number>;
  outcomeCounts: Record<string, number>;
}

export interface CaseProfile {
  category: CaseCategory;
  courtRegion: string;
  stage: ProcedureStage;
  objective: string;
  detained: boolean;
  priorRecord: boolean;
  notes?: string;
  selectedLawyerIds?: string[];
}

export interface ScoreDimension {
  key: string;
  label: string;
  score: number;
  max: number;
  reason: string;
}

export interface LawyerFitResult {
  lawyer: LawyerProfile;
  totalScore: number;
  fitLevel: "high" | "medium" | "low";
  dimensions: ScoreDimension[];
  matchingCases: PublicCase[];
  strengths: string[];
  cautions: string[];
  interviewQuestions: string[];
}

export interface FitReport {
  id: string;
  createdAt: string;
  request: CaseProfile;
  summary: string;
  disclaimer: string;
  results: LawyerFitResult[];
}

export const categoryLabels: Record<CaseCategory, string> = {
  fraud_money_laundering: "詐欺／洗錢／組織犯罪",
  drug: "毒品危害防制條例",
  sexual_offense: "妨害性自主",
  violence_family: "傷害／家暴／跟蹤騷擾",
  traffic_negligence: "交通過失／過失致死",
  weapons: "槍砲／強盜等重大刑案",
  financial_banking: "銀行法／金融犯罪",
  environmental: "廢棄物／環境刑案",
  other_criminal: "其他刑事",
};

export const stageLabels: Record<ProcedureStage, string> = {
  investigation: "偵查中",
  detention: "羈押／抗告／具保",
  first_instance: "一審",
  appeal: "二審／上訴",
  supreme_court: "最高法院",
  post_judgment: "執行／再審／判後程序",
};
